import { createActionGroup, createFeature, createReducer, createSelector, on, props } from "@ngrx/store";
import { AddressPublic, LocationPrivate, LocationPublic, LocationQuestionPublic, SegmentPublic, AddressType } from "../../../globalTypes/account.types";
import { AuthUserActions } from "./authUser.store";
import { UserInvite } from "../../../globalTypes/auth.types";
import { createEntityAdapter, EntityAdapter } from "@ngrx/entity";
import { Status } from "../../../globalTypes/base.types";

export interface AccountState {
  segment: SegmentPublic | null
  location: LocationPrivate | null
  locations: {[locationId: string]: LocationPublic} | {}
  availableLocations: LocationPublic[] | []
  availableSegments: SegmentPublic[] | []
  hasInitialized: boolean
  invitationsSent: UserInvite[] | []
  invitationsReceived: UserInvite[] | []
  statuses: Status[]
  userStatusId: string
}

export const AccountActions = createActionGroup({
  source: 'ACCOUNT',
  events: {
    'Set Segment': props<{ segment: SegmentPublic }>(),
    'Set Segment Location Questions': props<{ locationQuestions: LocationQuestionPublic[] }>(),
    'Set Location': props<{ location: LocationPrivate }>(),
    'Update Address': props<{ address: AddressPublic, addressType: AddressType }>(),
    'Set Locations': props<{ locations: {[locationId: string]: LocationPublic} }>(),
    'Set Available Locations': props<{ availableLocations: LocationPublic[] }>(),
    'Set Available Segments': props<{ availableSegments: SegmentPublic[] }>(),
    'Set Invitations Sent': props<{ invitationsSent: UserInvite[] }>(),
    'Set Invitations Received': props<{ invitationsReceived: UserInvite[] }>(),
    'Set Statuses': props<{ statuses: Status[] }>(),
    'Set User Status Id': props<{ statusId: string }>(),
  }
});

const adapter: EntityAdapter<LocationPublic> = createEntityAdapter<LocationPublic>();

const getAccountState = (): AccountState => ({ //adapter.getInitialState
// })
// const initialAccountState: AccountState = {
  segment: null,
  location: null,
  locations: {},
  availableLocations: [],
  availableSegments: [],
  hasInitialized: false,
  invitationsReceived: [],
  invitationsSent: [],
  statuses: [],
  userStatusId: ''
});

const accountReducer = createReducer(
  getAccountState(),
  on(AccountActions.setSegment, (state, { segment }) => ({ ...state, segment, hasInitialized: true })),
  on(AccountActions.setSegmentLocationQuestions, (state, { locationQuestions }) => {
    if(!state.segment) return state;
    const locQs = state.segment.locationQuestions ? [...state.segment?.locationQuestions, ...locationQuestions] : locationQuestions;
    return { ...state, segment: {...state.segment, locationQuestions: locQs}, hasInitialized: true}
  }),
  on(AccountActions.updateAddress, (state, { address, addressType }) => {
    const prop: keyof typeof state.location = (addressType === 'shipping' ? 'shippingAddress' : 'billingAddress') as keyof typeof state.location;
    if(!state.location || !state.location[prop]) return state;
    let current: AddressPublic = state.location?.[prop]
    const currentId = address.id
    if(currentId !== address.id){
      current = address;
    }
    return {...state, location: {...state.location, [prop]: current} as LocationPrivate}
  }),
  on(AccountActions.setLocation, (state, { location }) => ({ ...state, location, hasInitialized: true})),
  on(AccountActions.setLocations, (state, { locations }) => ({ ...state, locations, hasInitialized: true})),
  on(AccountActions.setAvailableLocations, (state, { availableLocations }) => {
    const locMap = availableLocations.reduce((a,loc,idx) => {a[loc.id] = loc; return a}, {} as {[locId:string]: LocationPublic})
    const availLocs = state.availableLocations.map(loc=>locMap[loc.id] || loc);
    const newAndUpdatedLocs = [...availLocs, ...availableLocations.filter(loc=>!availLocs.includes(loc))]
    return { ...state, availableLocations: newAndUpdatedLocs, hasInitialized: true}
  }),
  on(AccountActions.setInvitationsSent, (state, { invitationsSent }) => ({ ...state, invitationsSent})),
  on(AccountActions.setInvitationsReceived, (state, { invitationsReceived }) => ({ ...state, invitationsReceived})),
  on(AccountActions.setAvailableSegments, (state, { availableSegments }) => {
    const uniqueIds = new Set();
    const newAs = [...state.availableSegments, ...availableSegments].filter(obj => {
      if(!uniqueIds.has(obj.id)){
        uniqueIds.add(obj.id);
        return true;
      }
      return false;
    })
    return ({ ...state, availableSegments: newAs, hasInitialized: true})}),
  on(AccountActions.setStatuses, (state, {statuses}) => ({...state, statuses})),
  on(AccountActions.setUserStatusId, (state, {statusId}) => ({...state, userStatusId: statusId})),
  on(AuthUserActions.logOut, (state) => ({...getAccountState(), hasInitialized: true})),
)

export const accountFeature = createFeature({
  name: 'account',
  reducer: accountReducer,
  extraSelectors: ({selectStatuses}) => ({
    selectFulfillmentStatuses: createSelector(selectStatuses, (statuses) => statuses.filter(status=>status.for.includes('FULFILLMENTS'))),
    selectRefundStatuses: createSelector(selectStatuses, (statuses) => statuses.filter(status=>status.for.includes('REFUNDS'))),
    selectOrderStatuses: createSelector(selectStatuses, (statuses) => statuses.filter(status=>status.for.includes('ORDERS'))),
  })
})

